import { Service } from '@toeverything/infra';
import { type EditorContentInfo } from '../types/chat-message.d';
import type { BaseSelection, BlockModel } from '@blocksuite/affine/store';
import type {
  AgentBetaStore,
  ChatConfigType,
} from '../stores/agent-store-beta';
import type { AgentStoreService } from './agent-store-service';
import type { OrganizeService } from '../../organize';
import { WorkspaceService } from '../../workspace'; // 添加导入
import { MarkdownTransformer } from '@blocksuite/affine/widgets/linked-doc';
import { getStoreManager } from '@affine/core/blocksuite/manager/store';

export class AgentServiceBeta extends Service {
  constructor(
    private readonly _agentBetaStore: AgentBetaStore,
    private readonly _agentStoreService: AgentStoreService,
    private readonly _organizeService: OrganizeService
  ) {
    super();
    (
      window as unknown as Window & { __agentServiceBeta: AgentServiceBeta }
    ).__agentServiceBeta = this;
  }

  updateChatType(type: 'home' | 'doc') {
    this._updateChatConfig({
      type,
    });
  }

  setEditorDisplaySelections(selections: BaseSelection[]) {
    this._agentBetaStore.setEditorDisplaySelections(selections);
  }
  
  /**
   * 获取当前编辑选中 & 全文信息
   * 通过 AgentBetaStore 代理获取，保持接口兼容性
   */
  async getEditorContent(): Promise<EditorContentInfo> {
    return new Promise((resolve, reject) => {
      if (this._agentBetaStore) {
        this._agentBetaStore.getEditorContent().then(data => {
          resolve(data as EditorContentInfo);
        }, reject);
      } else {
        reject('agentBetaStore not found');
      }
    });
  }

  /**
   * 消费 LLM 命令
   * 处理来自 AI Agent 的命令，包括创建文档和更新内容
   * 
   * @param complexLLMCommand - LLM 命令对象
   * @param complexLLMCommand.doc_id - 文档 ID
   * @param complexLLMCommand.snap_content - Markdown 快照内容
   * @param complexLLMCommand.title_new_value - 新标题（可选）
   * @param complexLLMCommand.parent_id - 父文档 ID（可选）
   * @param complexLLMCommand.command - 命令类型：create/update/insert/delete
   * @param complexLLMCommand.uuid - 对话 ID
   */
  async consumeLLMCommand(complexLLMCommand: {
    doc_id: string;
    snap_content: string;
    title_new_value?: string;
    parent_id?: string;
    command: 'update' | 'insert' | 'delete' | 'create';
    uuid: string;
  }): Promise<void> {
    //
    console.log('####consumeLLMCommand', complexLLMCommand);

    if (complexLLMCommand.command === 'create') {
      return this._createDocOp({
        command: complexLLMCommand,
        doc_id: complexLLMCommand.doc_id,
        conversion_id: complexLLMCommand.uuid,
      }).catch(() => {
        // Error already logged in _createDocOp
      });
    }

    // 扫光
    this._organizeService.enableShimmer(complexLLMCommand.doc_id);

    // ✅ 使用 agentStoreService 创建 record
    this._agentStoreService
      .createRecordByDocId(
        complexLLMCommand.doc_id,
        'complex',
        {
          docBaseDataSnapshot: {
            snapshot: null,
            markdown: '',
            blockLineMapping: [],
            docId: '',
          },
          conversion_id: complexLLMCommand.uuid,
          commands: [],
        }
      )
      .catch(e => {
        console.error('Error creating agent record:', e);
      });

    // 更新标题
    if (complexLLMCommand.title_new_value) {
      this._organizeService
        .updateDocTitle(
          complexLLMCommand.doc_id,
          complexLLMCommand.title_new_value
        )
        .catch(e => {
          console.error('Error updating doc title:', e);
        });
    }

    // ✅ 使用 agentStoreService 更新 commands
    this._agentStoreService
      .updateCommands({
        doc_id: complexLLMCommand.doc_id,
        conversion_id: complexLLMCommand.uuid,
        commands: [complexLLMCommand],
      })
      .catch(e => {
        console.error('Error updating commands:', e);
      });
  }

  /**
   * 切换聊天关闭状态
   * @param type - 关闭类型，'end' 表示结束对话
   */
  toggleChatClose(type: string) {
    switch (type) {
      case 'end':
        // 重置 hasCreated
        this._updateChatConfig({
          hasCreated: false,
        });
        break;
    }
  }

  /**
   * 处理 generate_doc 指令（内部方法）
   * 创建新文档并执行初始化操作
   * 
   * @private
   */
  private async _createDocOp(params: {
    command: {
      doc_id: string;
      snap_content: string;
      title_new_value?: string;
      parent_id?: string;
      command: 'update' | 'insert' | 'delete' | 'create';
      uuid: string;
    };
    doc_id: string;
    conversion_id: string;
  }) {
    const config = this._agentBetaStore.chatConfig$.getValue();
    // 先更新默认成功
    this._updateChatConfig({
      hasCreated: true,
    });
    return this._organizeService
      .createArticleWithId({
        options: {
          title: params.command.title_new_value,
          doc_id: params.command.doc_id,
          parentDocId:
            params.command.parent_id === 'root'
              ? undefined
              : params.command.parent_id, // root 默认不传递
        },
        isFirstCreate: !config.hasCreated,
      })
      .then(async doc => {
        //
        console.log('####doc', doc);
        this._updateChatConfig({
          hasCreated: true,
        });

        this._organizeService.enableShimmer(doc.id);

        // ✅ 使用 agentStoreService 新建 record
        await this._agentStoreService.createRecordByDocId(doc.id, 'create', {
          docBaseDataSnapshot: {
            snapshot: null,
            markdown: '',
            blockLineMapping: [],
            docId: '',
          },
          commands: [],
          conversion_id: params.conversion_id,
        });

        // ✅ 使用 agentStoreService 更新 commands
        await this._agentStoreService.updateCommands({
          doc_id: doc.id,
          conversion_id: params.conversion_id,
          commands: [
            {
              ...params.command,
              doc_id: doc.id,
            },
          ],
        });
      })
      .catch(() => {
        // 如果当前文章新建是失败，释放掉首次打开的状态
        if (!config.hasCreated) {
          this._updateChatConfig({
            hasCreated: false,
          });
        }
      });
  }

  private _updateChatConfig(config: Partial<ChatConfigType>) {
    const currentConfig = this._agentBetaStore.chatConfig$.getValue();
    this._agentBetaStore.chatConfig$.next({
      ...currentConfig,
      ...config,
    });
  }

  /**
   * 监听编辑器选区变化
   * @param cb - 回调函数，当选区变化时触发
   */
  observeSelection(
    cb: (event: {
      selectedModels: BlockModel[];
      selectionMarkdown: string;
      timestamp: number;
    }) => void
  ) {
    this._agentBetaStore.observeSelectionChanged(cb);
  }

  /**
   * 校验并执行 chatContent 命令
   * 用于测试和规范化命令执行
   */
  async waitForFullSync() {
    const workspaceService = this.framework.get(WorkspaceService);
    // TODO  blob等附件没有保证同步
    await workspaceService.workspace.engine.doc.waitForSynced();
  }

  // 校验 chatContent 是否合法
  async specsChatCommand(specsContent: {
    doc_id: string;
    snap_content: string;
    title_new_value?: string;
    parent_id?: string;
    command: 'update' | 'insert' | 'delete' | 'create';
    uuid: string;
  }) {
    console.log('####specs-specsContent', specsContent);

    const { doc_id, snap_content, title_new_value, parent_id, command } =
      specsContent;

    if (command === 'create') {
      await this._organizeService.createArticleWithId({
        options: {
          title: title_new_value,
          doc_id,
          parentDocId: parent_id === 'root' ? undefined : parent_id, // root 默认不传递
        },
        isFirstCreate: false,
      });
    }

    const workspaceService = this.framework.get(WorkspaceService);
    await this.forceOverwriteDocWithMarkdown(
      workspaceService,
      doc_id,
      snap_content,
      title_new_value
    );

    return true;
  }

  /**
   * 通过 doc_id 全量使用 markdown 强行覆盖 doc 的函数
   * @param workspaceService - 工作区服务实例
   * @param docId - 要覆盖的文档 ID
   * @param markdown - 要覆盖的 markdown 内容
   * @param title - 可选的文档标题
   * @returns Promise<string> - 返回文档 ID
   * @throws Error - 当文档不存在或操作失败时抛出错误
   */
  async forceOverwriteDocWithMarkdown(
    workspaceService: WorkspaceService,
    docId: string,
    markdown: string,
    title?: string
  ): Promise<string> {
    const workspace = workspaceService.workspace;
    const collection = workspace.docCollection;

    // 获取目标文档
    const doc = collection.getDoc(docId)?.getStore();
    if (!doc) {
      throw new Error(`Document with ID ${docId} not found`);
    }

    try {
      // 获取页面块（affine:page）
      const pageBlocks = doc.getBlocksByFlavour('affine:page');
      if (pageBlocks.length === 0) {
        throw new Error('No page block found in the document');
      }

      const pageBlock = pageBlocks[0];

      // 删除页面块的所有子块（完全清空文档内容）
      pageBlock.model.children.forEach(child => {
        doc.deleteBlock(child);
      });

      // 添加新的 note 块
      const noteBlockId = doc.addBlock('affine:note', {}, pageBlock.id);

      // 使用 MarkdownTransformer 将 markdown 导入到 note 块
      await MarkdownTransformer.importMarkdownToBlock({
        doc,
        blockId: noteBlockId,
        markdown,
        extensions: getStoreManager().config.init().value.get('store'),
      });

      // 如果提供了标题，更新文档标题
      if (title) {
        const titleDelta = {
          '$blocksuite:internal:text$': true,
          delta: [
            {
              insert: title,
            },
          ],
        };
        // @ts-expect-error - BlockModel 类型定义不完整，但 title 属性在运行时存在
        pageBlock.model.title = titleDelta;
      }

      return doc.id;
    } catch (error) {
      console.error('Failed to overwrite document with markdown:', error);
      throw new Error(
        `Failed to overwrite document: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}
